import httpService from "./httpService";

/**
 * GET /v3/investor/ibkr-status (PWA-2810). Contract mirrors
 * plutus-services shared-lib/src/types/ibkrStatusV3.ts.
 *
 * AVAILABLE: linked and the advisor's ib-service is healthy.
 * RESET_PERIOD: linked and IBKR is inside its scheduled reset window.
 * UNAVAILABLE: linked, outside a reset window, ib-service unreachable or unhealthy.
 * NOT_LINKED: no IBKR account is linked to the selected user.
 */
export const IBKR_CONNECTION_STATUSES = [
  "AVAILABLE",
  "RESET_PERIOD",
  "UNAVAILABLE",
  "NOT_LINKED",
] as const;

export type IbkrConnectionStatus = typeof IBKR_CONNECTION_STATUSES[number];

export interface IbkrStatusV3Response {
  serverTime: string;
  status: IbkrConnectionStatus;
  asOf: string | null;
  resetPeriod: { endsAt: string } | null;
}

/** Statuses where account data may be delayed or missing. */
export const IBKR_DEGRADED_STATUSES: readonly IbkrConnectionStatus[] = ["RESET_PERIOD", "UNAVAILABLE"];

export function isIbkrStatusDegraded(status: IbkrConnectionStatus | null): boolean {
  return status !== null && IBKR_DEGRADED_STATUSES.includes(status);
}

const invalid = (): never => {
  throw new Error("The IBKR status response could not be verified.");
};

function isStatus(value: unknown): value is IbkrConnectionStatus {
  return typeof value === "string" && (IBKR_CONNECTION_STATUSES as readonly string[]).includes(value);
}

function text(value: unknown): string {
  return typeof value === "string" ? value : invalid();
}

export const ibkrStatusService = {
  async getInvestorIbkrStatus(signal?: AbortSignal): Promise<IbkrStatusV3Response> {
    const response = await httpService.get<unknown>("/v3/investor/ibkr-status", { signal });
    const raw = response.data;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return invalid();
    const record = raw as Record<string, unknown>;
    if (!isStatus(record.status)) return invalid();
    const resetPeriod = record.resetPeriod;
    return {
      serverTime: text(record.serverTime),
      status: record.status,
      asOf: record.asOf === null || record.asOf === undefined ? null : text(record.asOf),
      resetPeriod: resetPeriod && typeof resetPeriod === "object"
        ? { endsAt: text((resetPeriod as Record<string, unknown>).endsAt) }
        : null,
    };
  },
};

export default ibkrStatusService;
